import { ALL_COLLECTIONS_OFF } from '../api/wire';
import type { IndexSummary } from '../api/summary';
import { displayDate } from '../api/adapter';
import { monthLabel, monthOf } from '../api/month';
import type { WireCapabilities } from '../api/wire';

import { ListRow } from '../components/navigation/ListRow.jsx';
import { SectionHeading } from '../components/composed/SectionHeading';
import { Pressable } from '../components/a11y/Pressable';
import { NOTE_SEPARATOR } from '../lib/text';

export type Destination =
  | 'today'
  | 'week'
  | 'month'
  | 'future'
  | 'reflections'
  | 'habits'
  | 'commonplace'
  | 'reading'
  | 'gratitude'
  | 'filters'
  | 'settings';

/** The front-of-journal table of contents.
 *
 *  Every row carries a live fact rather than a description — `02 AUG · 4 open`,
 *  not "your daily log". That is what makes this a table of contents for *this*
 *  journal instead of a list of features, and it is the whole reason the Index
 *  is worth opening.
 *
 *  Section order follows the design: the four logs first, then collections, then
 *  filters with Settings among them. Collections come from `/v1/capabilities`,
 *  never hardcoded, so one disabled server-side disappears rather than leading
 *  to a 409 `collection_disabled`.
 *
 *  No sigils here. `ListRow` renders `{sigil}{label}` with no separator, so a
 *  sigil reads as `sSettings` — and the design's Index does not use them anyway.
 *  They belong on the filters list, where `/Personal` is the point.
 */
export function Index({
  journalDate,
  capabilities,
  summary,
  onOpen,
}: {
  journalDate: string;
  capabilities: WireCapabilities | null;
  /** Fetched by the app, not here: the rail needs the same facts, and at rail
   *  widths both are on screen at once. */
  summary: IndexSummary | null;
  onOpen: (where: Destination) => void;
}) {
  const collections = capabilities?.collections ?? ALL_COLLECTIONS_OFF;
  const month = monthOf(journalDate);
  const fact = (value: number | null, one: string, many = `${one}s`) =>
    value === null ? '' : `${value} ${value === 1 ? one : many}`;

  return (
    <main style={{ padding: 'var(--bujo-gutter)', maxWidth: 560, margin: '0 auto' }}>
      <header style={header}>
        <div style={eyebrow}>BUJO</div>
        <h1 style={h1}>INDEX</h1>
        {/* Where this client points, when it last read, and how many calendars
            are attached — the three things worth knowing at a glance. */}
        <div style={subtitle}>
          {summary
            ? [
                summary.server,
                `synced ${summary.syncedAt}`,
                summary.calendars === null
                  ? 'no calendar'
                  : fact(summary.calendars, 'calendar'),
              ].join(NOTE_SEPARATOR)
            : 'reading…'}
        </div>
      </header>

      <section>
        <SectionHeading active>LOGS</SectionHeading>
        <Pressable label="Daily Log" onPress={() => onOpen('today')}>
          <ListRow
            label="Daily Log"
            meta={`${displayDate(journalDate).slice(0, 6)} · ${fact(summary?.openToday ?? null, 'open', 'open')}`}
          />
        </Pressable>
        <Pressable label="Weekly Log" onPress={() => onOpen('week')}>
          <ListRow
            label="Weekly Log"
            meta={`W${summary?.isoWeek ?? ''} · ${fact(summary?.habitCount ?? null, 'habit')}`}
          />
        </Pressable>
        <Pressable label="Monthly Log" onPress={() => onOpen('month')}>
          <ListRow
            label="Monthly Log"
            meta={`${monthLabel(month.month).slice(0, 3)} · ${summary?.monthInbox ?? 0} in inbox`}
          />
        </Pressable>
        {/* Named because the journal has one, dimmed because this client does
            not render it yet. Silence would read as though it did not exist. */}
        <Pressable label="Future Log" onPress={() => onOpen('future')}>
          <ListRow label="Future Log" meta="six months forward" />
        </Pressable>
        {/* Sits with the logs rather than the collections: reflections are a
            subsystem, not a collection, so there is no capability to gate on
            and nothing to disable server-side. */}
        <Pressable label="Reflections" onPress={() => onOpen('reflections')}>
          <ListRow label="Reflections" meta="everything written, newest first" />
        </Pressable>
      </section>

      <section style={{ paddingTop: 'var(--bujo-space-4)' }}>
        <SectionHeading muted note={capabilities ? 'ENABLED' : ''}>COLLECTIONS</SectionHeading>
        {/* Habits opens its own month grid. It used to redirect to the weekly
            log, which shows seven days for a collection that is a month. */}
        {collections['habits'] ? (
          <Pressable label="Habits" onPress={() => onOpen('habits')}>
            <ListRow
              label="Habits"
              meta={`${summary?.habitCount ?? 0} · a month at a time`}
            />
          </Pressable>
        ) : null}
        {collections['reading_log'] ? (
          <Pressable label="Reading Log" onPress={() => onOpen('reading')}>
            <ListRow label="Reading Log" meta={fact(summary?.readingTitles ?? null, 'title')} />
          </Pressable>
        ) : null}
        {collections['gratitude_log'] ? (
          <Pressable label="Gratitude Log" onPress={() => onOpen('gratitude')}>
            <ListRow
              label="Gratitude Log"
              meta={`${monthLabel(month.month).slice(0, 3)} · ${summary?.gratitudeWritten ?? 0}/${summary?.gratitudeDays ?? month.days.length}`}
            />
          </Pressable>
        ) : null}
        {collections['commonplace_books'] ? (
          <Pressable label="Commonplace Books" onPress={() => onOpen('commonplace')}>
            <ListRow label="Commonplace Books" meta="quotes, lists and notes" />
          </Pressable>
        ) : null}
      </section>

      <section style={{ paddingTop: 'var(--bujo-space-4)' }}>
        <SectionHeading muted>FILTERS</SectionHeading>
        <Pressable label="Streams, projects and contexts" onPress={() => onOpen('filters')}>
          <ListRow
            label="Streams · Projects · Contexts"
            meta={
              summary
                ? `${summary.streams ?? 0} · ${summary.projects ?? 0} · ${summary.contexts ?? 0}`
                : ''
            }
          />
        </Pressable>
        <Pressable label="Settings" onPress={() => onOpen('settings')}>
          <ListRow label="Settings" meta="theme · calendars" />
        </Pressable>
      </section>
    </main>
  );
}

const header = {
  paddingBottom: 'var(--bujo-space-2)',
  marginBottom: 'var(--bujo-space-4)',
  borderBottom: '1px solid var(--bujo-rule-heavy)',
};
const eyebrow = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-eyebrow)',
  color: 'var(--bujo-text-meta)',
  paddingBottom: 'var(--bujo-space-2)',
};
const h1 = {
  margin: 0,
  font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1.1 var(--bujo-font-mono)',
  color: 'var(--bujo-accent)',
};
const subtitle = {
  paddingTop: 'var(--bujo-space-1)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
};
